import { useQuery } from '@apollo/client';
import type { DescriptionPage_DifficultyLevel as DifficultyLevel } from '__generated__/graphql-types';

import { useParams } from 'js/lib/useRouter';

import type { TopReview } from 'bundles/reviews-common/types/reviewsSharedTypes';
import reviewsCourseHeaderDataQuery from 'bundles/reviews/queries/ReviewsCourseHeaderData.graphql';
import reviewsPageQuery from 'bundles/reviews/queries/ReviewsPage.graphql';
import reviewsProductCardDataQuery from 'bundles/reviews/queries/ReviewsProductCardData.graphql';
import type {
  ReviewsCourseHeaderDataQuery as ReviewsCourseHeaderDataQueryResult,
  ReviewsCourseHeaderDataQueryVariables,
} from 'bundles/reviews/queries/__generated__/ReviewsCourseHeaderData';
import type {
  ReviewsPageCdpMetadataFragment,
  ReviewsPageCourseFragment,
  ReviewsPageQuery,
  ReviewsPageQueryVariables,
  ReviewsPageSchemaReviewFragment,
} from 'bundles/reviews/queries/__generated__/ReviewsPage';
import type {
  ReviewsProductCardDataQuery as ReviewsProductCardDataQueryResult,
  ReviewsProductCardDataQueryVariables,
} from 'bundles/reviews/queries/__generated__/ReviewsProductCardData';
import { NotFoundError } from 'bundles/ssr/lib/errors/directives';
import inServerContext from 'bundles/ssr/util/inServerContext';

type ReviewsPageCourseConnection = ReviewsPageQuery['NaptimeQueries']['XdpV1Resource']['course'];
type ReviewsPageCourseElement = NonNullable<NonNullable<ReviewsPageCourseConnection>['elements'][number]>;

export type ReviewsPageCourseInfo = ReviewsPageCourseFragment & {
  xdpMetadata: ReviewsPageCdpMetadataFragment;
};

export type ReviewsPageCdpMetadata = ReviewsPageCdpMetadataFragment['cdpMetadata'];
export type ReviewsPageSchemaReview = ReviewsPageSchemaReviewFragment;

type ReviewsHeaderData = {
  averageFiveStarRating?: number | null;
  courseName: string;
  description?: string | null;
  difficultyLevel?: DifficultyLevel | null;
  imageUrl?: string | null;
  marketingProductType?: string | null;
  partnerLogoUrl?: string | null;
  partnerName?: string | null;
  ratingCount?: number | null;
  ratingCountsByStar?: {
    fiveStarRatingCount?: number | null;
    fourStarRatingCount?: number | null;
    threeStarRatingCount?: number | null;
    twoStarRatingCount?: number | null;
    oneStarRatingCount?: number | null;
  } | null;
  skillTags: string[];
  totalDuration?: string | null;
};

type ReviewsPageDataResult = {
  loading: boolean;
  error?: Error;
  notFound: boolean;
  courseInfo: ReviewsPageCourseInfo | null;
  topReviews: TopReview[];
  schemaReviews: ReviewsPageSchemaReview[] | null;
  headerData: ReviewsHeaderData;
};

const isDefined = <T>(value: T | null | undefined): value is T => value != null;

const isReviewsPageCourseInfo = (course: ReviewsPageCourseElement): course is ReviewsPageCourseInfo =>
  course.xdpMetadata.__typename === 'XdpV1_cdpMetadataMember';

export function useReviewsPageData(): ReviewsPageDataResult {
  const params = useParams();
  const slug = params?.slug;

  const { data, loading, error } = useQuery<ReviewsPageQuery, ReviewsPageQueryVariables>(reviewsPageQuery, {
    variables: { slug: slug ?? '' },
    skip: !slug,
    context: {
      ssrBlocking: true,
    },
  });

  const {
    data: courseHeaderData,
    loading: courseHeaderLoading,
    error: courseHeaderError,
  } = useQuery<ReviewsCourseHeaderDataQueryResult, ReviewsCourseHeaderDataQueryVariables>(
    reviewsCourseHeaderDataQuery,
    {
      variables: { slug: slug ?? '' },
      skip: !slug,
      context: {
        clientName: 'gatewayGql',
      },
    }
  );

  const elements = (data?.NaptimeQueries?.XdpV1Resource?.course?.elements ?? []).filter(isDefined);
  const courseInfo = elements.find(isReviewsPageCourseInfo) ?? null;
  const cdpMetadata = courseInfo?.xdpMetadata.cdpMetadata ?? null;
  const topReviews =
    cdpMetadata?.topReviews?.filter(
      (review): review is TopReview =>
        review?.averageFiveStarRating != null &&
        review.timestamp != null &&
        review.authorName != null &&
        review.comment != null
    ) ?? [];
  const schemaReviews = courseInfo?.fetchProductReviews?.elements?.filter(isDefined) ?? null;
  const courseId = courseInfo?.id?.split('~')[1] ?? '';

  const {
    data: productCardData,
    loading: productCardLoading,
    error: productCardError,
  } = useQuery<ReviewsProductCardDataQueryResult, ReviewsProductCardDataQueryVariables>(reviewsProductCardDataQuery, {
    variables: {
      productCardKeys: courseId ? [{ id: courseId, canonicalType: 'COURSE' as const }] : [],
    },
    skip: !courseId,
    context: {
      clientName: 'gatewayGql',
    },
  });

  const notFound = !loading && !!slug && elements.length === 0;

  if (notFound && inServerContext) {
    throw new NotFoundError();
  }

  const descriptionPage = courseHeaderData?.DescriptionPage?.queryCoursePage;
  const productCardEntry = productCardData?.ProductCard?.queryByKeys?.[0];
  const productCanonical =
    productCardEntry?.productTypeAttributes?.__typename === 'ProductCard_Course'
      ? productCardEntry.productTypeAttributes.canonical
      : null;

  const headerData: ReviewsHeaderData = {
    averageFiveStarRating:
      descriptionPage?.ratings?.averageFiveStarRating ?? cdpMetadata?.ratings?.averageFiveStarRating ?? null,
    courseName: productCanonical?.name ?? courseInfo?.name ?? '',
    description: descriptionPage?.description ?? cdpMetadata?.description ?? null,
    difficultyLevel: descriptionPage?.difficultyLevel ?? null,
    imageUrl: productCanonical?.promoPhotoUrl ?? null,
    marketingProductType: productCardEntry?.marketingProductType ?? null,
    partnerLogoUrl:
      productCanonical?.partners?.[0]?.squareLogo ??
      descriptionPage?.partners?.[0]?.squareLogo ??
      cdpMetadata?.partners?.[0]?.squareLogo ??
      cdpMetadata?.partners?.[0]?.logo ??
      null,
    partnerName:
      productCanonical?.partners?.[0]?.name ??
      descriptionPage?.partners?.[0]?.name ??
      cdpMetadata?.partners?.[0]?.name ??
      null,
    ratingCount: descriptionPage?.ratings?.ratingCount ?? cdpMetadata?.ratings?.ratingCount ?? null,
    ratingCountsByStar: descriptionPage?.ratingCountsByStar ?? null,
    skillTags: (descriptionPage?.skillTags ?? []).map((tag) => tag?.name?.trim()).filter((tag): tag is string => !!tag),
    totalDuration: descriptionPage?.material?.totalDuration ?? null,
  };

  return {
    loading: !!slug && (loading || courseHeaderLoading || (!!courseId && productCardLoading)),
    error: error ?? courseHeaderError ?? productCardError ?? undefined,
    notFound,
    courseInfo,
    topReviews,
    schemaReviews,
    headerData,
  };
}
